
import React, { useState, useEffect } from 'react';
import { Region, TherapeuticArea, RegulatoryPathway, ProductType, Consultancy } from '../../types';
import { getSuggestedConsultancies } from '../../services/automationService';
import { ArrowRight, ArrowLeft, Check, Search, Zap, Users, Clock, Shield, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const RFQBuilder: React.FC = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    
    // Form Data
    const [title, setTitle] = useState('');
    const [regions, setRegions] = useState<Region[]>([]);
    const [ta, setTa] = useState<TherapeuticArea | ''>('');
    const [pathway, setPathway] = useState<RegulatoryPathway | ''>('');
    const [budget, setBudget] = useState('');
    const [description, setDescription] = useState('');
    const [deadline, setDeadline] = useState('');
    
    // Matching State
    const [suggestions, setSuggestions] = useState<{consultancy: Consultancy, match: any}[]>([]);
    const [selectedVendors, setSelectedVendors] = useState<string[]>([]);

    // Trigger matching when reaching Step 3
    useEffect(() => {
        if (step === 3) {
            const matches = getSuggestedConsultancies({
                regions,
                therapeuticArea: ta as TherapeuticArea,
                pathway: pathway as RegulatoryPathway
            });
            setSuggestions(matches);
            // Auto-select top 3 matches
            setSelectedVendors(matches.slice(0, 3).map(m => m.consultancy.id));
        }
    }, [step, regions, ta, pathway]);

    const handleNext = () => setStep(prev => prev + 1);
    const handleBack = () => setStep(prev => prev - 1);
    
    const toggleRegion = (r: Region) => {
        setRegions(prev => prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]);
    };

    const toggleVendor = (id: string) => {
        setSelectedVendors(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const handleSubmit = () => {
        // Logic to save RFQ would go here
        alert(`RFQ "${title}" sent to ${selectedVendors.length} consultancies!`);
        navigate('/client-dashboard');
    };

    return (
        <div className="max-w-4xl mx-auto px-4 py-12">
            {/* Progress Stepper */}
            <div className="mb-8">
                <div className="flex items-center justify-between mb-2">
                    {['Project Scope', 'Details & Budget', 'Smart Matching', 'Review'].map((label, idx) => (
                        <div key={label} className={`flex flex-col items-center relative z-10 w-1/4 ${idx + 1 <= step ? 'text-blue-600' : 'text-slate-400'}`}>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mb-2 transition-colors ${idx + 1 <= step ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'}`}>
                                {idx + 1 < step ? <Check className="w-5 h-5" /> : idx + 1}
                            </div>
                            <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
                        </div>
                    ))}
                    {/* Connector Line */}
                    <div className="absolute top-4 left-0 w-full h-0.5 bg-slate-200 -z-0 transform translate-y-3">
                         <div className="h-full bg-blue-600 transition-all duration-300" style={{width: `${((step - 1) / 3) * 100}%`}}></div>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
                <div className="p-8 min-h-[400px]">
                    
                    {/* STEP 1: SCOPE */}
                    {step === 1 && (
                        <div className="space-y-6">
                            <h2 className="text-2xl font-bold text-slate-900">Define Project Scope</h2>
                            
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Project Title</label>
                                <input 
                                    type="text" 
                                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-blue-500"
                                    placeholder="e.g. Orphan Designation Strategy for Rare Oncology Asset"
                                    value={title}
                                    onChange={e => setTitle(e.target.value)}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-3">Target Health Authorities</label>
                                <div className="flex flex-wrap gap-2">
                                    {Object.values(Region).map(r => (
                                        <button
                                            key={r}
                                            onClick={() => toggleRegion(r)}
                                            className={`px-4 py-2 rounded-full text-sm border transition-all ${regions.includes(r) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                                        >
                                            {r.split('(')[0]}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Therapeutic Area</label>
                                    <select 
                                        className="w-full p-3 border border-slate-300 rounded-lg focus:ring-blue-500"
                                        value={ta}
                                        onChange={e => setTa(e.target.value as TherapeuticArea)}
                                    >
                                        <option value="">Select Area...</option>
                                        {Object.values(TherapeuticArea).map(a => <option key={a} value={a}>{a}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Primary Pathway</label>
                                    <select 
                                        className="w-full p-3 border border-slate-300 rounded-lg focus:ring-blue-500"
                                        value={pathway}
                                        onChange={e => setPathway(e.target.value as RegulatoryPathway)}
                                    >
                                        <option value="">Select Pathway...</option>
                                        {Object.values(RegulatoryPathway).map(p => <option key={p} value={p}>{p}</option>)}
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* STEP 2: DETAILS */}
                    {step === 2 && (
                        <div className="space-y-6">
                            <h2 className="text-2xl font-bold text-slate-900">Details & Timeline</h2>
                            
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Project Description</label>
                                <textarea 
                                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-blue-500 h-32"
                                    placeholder="Describe the asset, stage of development, and specific deliverables needed..."
                                    value={description}
                                    onChange={e => setDescription(e.target.value)}
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Budget Indication (Optional)</label>
                                    <select 
                                        className="w-full p-3 border border-slate-300 rounded-lg focus:ring-blue-500"
                                        value={budget}
                                        onChange={e => setBudget(e.target.value)}
                                    >
                                        <option value="">No preference</option>
                                        <option value="< $50k">&lt; $50k</option>
                                        <option value="$50k - $150k">$50k - $150k</option>
                                        <option value="$150k - $500k">$150k - $500k</option>
                                        <option value="> $500k">&gt; $500k</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Proposal Deadline</label>
                                    <input 
                                        type="date" 
                                        className="w-full p-3 border border-slate-300 rounded-lg focus:ring-blue-500"
                                        value={deadline}
                                        onChange={e => setDeadline(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* STEP 3: MATCHING */}
                    {step === 3 && (
                        <div className="space-y-6">
                            <div className="flex items-center justify-between">
                                <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                                    <Zap className="w-6 h-6 text-yellow-500 fill-current" /> Smart Matching
                                </h2>
                                <div className="text-sm text-slate-500">
                                    {selectedVendors.length} selected
                                </div>
                            </div>
                            
                            <p className="text-slate-500">
                                Our matching engine identified <strong>{suggestions.length} consultancies</strong> that fit your criteria. 
                                Review and select who receives this RFQ.
                            </p>

                            <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                                {suggestions.map(({consultancy, match}) => (
                                    <div 
                                        key={consultancy.id}
                                        onClick={() => toggleVendor(consultancy.id)}
                                        className={`flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all ${selectedVendors.includes(consultancy.id) ? 'border-blue-600 bg-blue-50' : 'border-slate-200 hover:border-blue-300'}`}
                                    >
                                        <div className="flex-shrink-0 mr-4">
                                            <div className={`w-6 h-6 rounded-full border flex items-center justify-center ${selectedVendors.includes(consultancy.id) ? 'bg-blue-600 border-blue-600' : 'bg-white border-slate-300'}`}>
                                                {selectedVendors.includes(consultancy.id) && <Check className="w-4 h-4 text-white" />}
                                            </div>
                                        </div>
                                        
                                        <img src={consultancy.logoUrl} alt={consultancy.name} className="w-12 h-12 rounded-lg object-cover mr-4" />
                                        
                                        <div className="flex-grow">
                                            <div className="flex items-center gap-2">
                                                <h3 className="font-bold text-slate-900">{consultancy.name}</h3>
                                                {match.score > 80 && (
                                                    <span className="bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full font-bold">
                                                        {match.score}% Match
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-xs text-slate-500 truncate max-w-md">{consultancy.tagline}</p>
                                            <div className="mt-1 flex gap-2">
                                                {match.reasons.map((r: string, i: number) => (
                                                    <span key={i} className="text-[10px] bg-white border border-slate-200 px-1.5 py-0.5 rounded text-slate-600">
                                                        {r}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="text-right flex-shrink-0 text-xs text-slate-500">
                                            <div><Shield className="w-3 h-3 inline mr-1"/> {consultancy.verifiedApprovals} Approvals</div>
                                            <div><Clock className="w-3 h-3 inline mr-1"/> {consultancy.responseTime}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* STEP 4: REVIEW */}
                    {step === 4 && (
                        <div className="space-y-6">
                             <h2 className="text-2xl font-bold text-slate-900">Review & Send</h2>
                             
                             <div className="bg-slate-50 rounded-lg p-6 border border-slate-200 space-y-4">
                                 <div className="grid grid-cols-2 gap-4">
                                     <div>
                                         <span className="text-xs font-bold text-slate-500 uppercase">Project</span>
                                         <p className="font-medium">{title}</p>
                                     </div>
                                     <div>
                                         <span className="text-xs font-bold text-slate-500 uppercase">Recipients</span>
                                         <p className="font-medium">{selectedVendors.length} Consultancies</p>
                                     </div>
                                     <div>
                                         <span className="text-xs font-bold text-slate-500 uppercase">Pathway</span>
                                         <p className="font-medium">{pathway || 'N/A'}</p>
                                     </div>
                                     <div>
                                         <span className="text-xs font-bold text-slate-500 uppercase">Deadline</span>
                                         <p className="font-medium">{deadline || 'None'}</p>
                                     </div>
                                 </div>
                             </div>

                             <div className="bg-blue-50 border border-blue-100 p-4 rounded-lg flex gap-3">
                                 <Zap className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                                 <div className="text-sm text-blue-800">
                                     <strong>Automated Routing:</strong> Once sent, we will notify the selected consultancies immediately. 
                                     If they don't view the RFQ within 48 hours, we will send an automated reminder.
                                 </div>
                             </div>
                        </div>
                    )}
                </div>

                {/* Footer Controls */}
                <div className="bg-slate-50 px-8 py-4 border-t border-slate-200 flex justify-between">
                    <button 
                        onClick={handleBack} 
                        disabled={step === 1}
                        className="px-4 py-2 text-slate-600 font-medium hover:text-slate-900 disabled:opacity-50 flex items-center"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2"/> Back
                    </button>
                    
                    {step < 4 ? (
                        <button 
                            onClick={handleNext}
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 flex items-center"
                        >
                            Continue <ArrowRight className="w-4 h-4 ml-2"/>
                        </button>
                    ) : (
                        <button 
                            onClick={handleSubmit}
                            className="px-6 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 flex items-center"
                        >
                            Post RFQ <Zap className="w-4 h-4 ml-2"/>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};
